import React from 'react';
import { Helmet } from 'react-helmet-async';

// Site-wide constants
const SITE_NAME = 'Little House on the Table';
const SITE_URL = 'https://littlehouseonthetable.com';
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/n5mw12s5_IMG_6869.JPG';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/ob2xdoj4_279749100_449900870272747_652948079724847386_n.jpg';

// Default meta data
const defaults = {
  title: 'Little House on the Table | Nebraska Family Ranch',
  description: 'A 7th generation Nebraska family ranch offering handmade goat milk soaps, jams, jellies, salsas, and aprons. Sustainable agriculture, ethically raised goats, and products crafted with love.',
  keywords: 'goat milk soap, Nebraska ranch, handmade soap, jams, jellies, salsas, aprons, sustainable agriculture, family farm, Crawford Nebraska, natural soap, homemade products'
};

/**
 * SEO Component - Handles all meta tags for each page
 * @param {string} title - Page title (will be appended with site name)
 * @param {string} description - Meta description (140-160 chars recommended)
 * @param {string} path - URL path (e.g., '/about')
 * @param {string} image - OG image URL (defaults to ranch photo)
 * @param {string} type - OG type (default: 'website')
 * @param {object} structuredData - JSON-LD structured data object
 * @param {boolean} noIndex - Set to true to prevent indexing
 */
export const SEO = ({
  title,
  description = defaults.description,
  path = '',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  structuredData = null,
  noIndex = false,
  keywords = defaults.keywords
}) => {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : defaults.title;
  const canonicalUrl = `${SITE_URL}${path}`;
  const ogImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph Tags */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} - Nebraska Family Ranch`} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} - Nebraska Family Ranch`} />
      
      {/* Additional SEO Tags */}
      <meta name="author" content={SITE_NAME} />
      <meta name="geo.region" content="US-NE" />
      <meta name="geo.placename" content="Crawford, Nebraska" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// LocalBusiness Schema for the ranch
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://littlehouseonthetable.com/#business",
  "name": "Little House on the Table",
  "url": "https://littlehouseonthetable.com/",
  "logo": LOGO_URL,
  "image": DEFAULT_OG_IMAGE,
  "description": "A 7th generation Nebraska family ranch offering handmade goat milk soaps, jams, jellies, salsas, and aprons crafted with sustainable practices and love.",
  "telephone": "+1-308-665-5400",
  "email": "wohlersbeef@gmail.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Crawford",
    "addressRegion": "NE",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 42.6858,
    "longitude": -103.4183
  },
  "areaServed": ["Crawford", "Nebraska", "United States"],
  "priceRange": "$$",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    "opens": "00:00",
    "closes": "23:59"
  },
  "sameAs": [
    "https://www.facebook.com/LittleHouseontheTable",
    "https://littlehouseonthetable.square.site"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Ranch Products",
    "itemListElement": [
      {
        "@type": "OfferCatalog",
        "name": "Goat Milk Soap",
        "itemListElement": {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Handmade Goat Milk Soap"
          }
        }
      },
      {
        "@type": "OfferCatalog",
        "name": "Jams, Jellies & Salsas",
        "itemListElement": {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Homemade Jams, Jellies & Salsas"
          }
        }
      },
      {
        "@type": "OfferCatalog",
        "name": "Aprons",
        "itemListElement": {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Handmade Aprons"
          }
        }
      }
    ]
  }
});

// Breadcrumb Schema generator
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://littlehouseonthetable.com${item.path}`
  }))
});

// FAQ Schema generator
export const getFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Product Schema generator
export const getProductSchema = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": product.name,
  "description": product.description,
  "image": product.image,
  "brand": {
    "@type": "Brand",
    "name": "Little House on the Table"
  },
  "manufacturer": {
    "@type": "Organization",
    "name": "Little House on the Table",
    "url": "https://littlehouseonthetable.com"
  },
  "offers": {
    "@type": "Offer",
    "url": product.url,
    "availability": "https://schema.org/InStock",
    "seller": {
      "@type": "Organization",
      "name": "Little House on the Table"
    }
  }
});

// Organization Schema
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Little House on the Table",
  "url": "https://littlehouseonthetable.com",
  "logo": LOGO_URL,
  "description": "A 7th generation Nebraska family ranch offering handmade goat milk products, jams, jellies, salsas, and aprons.",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Crawford",
    "addressRegion": "NE",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-308-665-5400",
    "email": "wohlersbeef@gmail.com",
    "contactType": "customer service"
  },
  "sameAs": [
    "https://www.facebook.com/LittleHouseontheTable"
  ]
});

export default SEO;